'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type * as z from 'zod';

import { usePlanByStripePriceId } from '@/hooks/queries/pricing';
import { useSignIn } from '@/hooks/use-auth';

import { loginSchema } from '@/schemas/auth';

import Logo from '@/components/common/logo';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { ControlledPasswordInput } from '@/components/ui/form/controlled-password-input';
import { Input } from '@/components/ui/input';

import { createCheckoutURL } from '@/utils/checkout';
import { paths } from '@/constants/paths';

type LoginFormValues = z.infer<typeof loginSchema>;

export default function LoginPageContent() {
  const searchParams = useSearchParams();
  const priceId = searchParams.get('price_id');

  const { mutate: signIn, isPending } = useSignIn();
  const { data: plan } = usePlanByStripePriceId(priceId || '');

  const isFreePlan = plan && plan.price === 0;
  // const displayPlanName = plan?.display_name || 'plan';

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = (values: LoginFormValues) => {
    signIn(values, {
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: async () => {
        toast.success('Logged in successfully');
        if (plan && !isFreePlan) {
          const checkoutUrl = createCheckoutURL({
            email: values.email,
            priceId: plan.stripe_price_id!,
            source: 'self',
          });
          window.location.replace(checkoutUrl);
        } else {
          window.location.replace(paths.dashboard.index);
        }
      },
    });
  };

  return (
    <div className='flex min-h-screen items-center justify-center bg-neutral-50 px-4 py-12 dark:bg-neutral-900 sm:px-6 lg:px-8'>
      <Card className='w-full max-w-md'>
        <CardHeader className='space-y-1'>
          <Logo containerStyles='mx-auto mb-4 w-full' width={180} />
          <CardTitle className='text-2xl font-bold'>Login</CardTitle>
          <CardDescription>
            Enter your email and password to login to your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Show plan info alerts */}
          {/* {isPlanLoading && priceId ? (
            <div className='mb-4 space-y-2'>
              <Skeleton className='h-4 w-32' />
              <Skeleton className='h-4 w-full' />
              <Skeleton className='h-4 w-3/4' />
            </div>
          ) : isFreePlan ? (
            <Alert className='mb-4'>
              <AlertTitle>Welcome back!</AlertTitle>
              <AlertDescription>
                After you login, you'll get instant access to your{' '}
                <strong>{displayPlanName}</strong> plan - completely free! No
                credit card needed.
              </AlertDescription>
            </Alert>
          ) : (
            plan?.id && (
              <Alert className='mb-4'>
                <AlertTitle>Welcome back!</AlertTitle>
                <AlertDescription>
                  After you login, we'll redirect you to checkout to complete
                  your <strong>{displayPlanName}</strong> plan subscription.
                </AlertDescription>
              </Alert>
            )
          )} */}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder='m@example.com' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <ControlledPasswordInput
                name='password'
                label='Password'
                placeholder='Enter your password'
              />
              <div className='flex items-center justify-end'>
                <Link
                  href={paths.auth.forgotPassword}
                  className='text-sm text-primary hover:underline'
                >
                  Forgot password?
                </Link>
              </div>
              <Button type='submit' className='w-full' isLoading={isPending}>
                Login
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
